/**
 * Re-reads the Pro trial's source when a running trial ends (#2816): the
 * server closes the Pro features at `endsAt`, and a screen left open would
 * otherwise keep the banner, the «Pro · trial» marks and the open verdicts
 * until the next window focus or entry. Each shell passes its own re-read —
 * `/feature-flags` in the web app, the session in the CRM iframe.
 *
 * Once just past `endsAt`, then every minute until the answer says the trial
 * ended: a failed read is asked again, not taken as final. The first answer
 * after the end says so, so the repeats run only while reads fail.
 *
 * The start is not here: the web app asks from a visible tab
 * (`refetchInterval`), the iframe on focus (`EmbeddedOrgContext`).
 */
import { useEffect } from 'react';
import type { ProTrialView } from '@aica/api-types/schemas';

/** Past the end, until a re-read says the trial ended. */
const END_RETRY_MS = 60 * 1000;
/** A beat past the end, so the server already reads the trial as ended. */
const AFTER_END_MS = 1000;
/** `setTimeout`'s ceiling (~24.8 days): a later end is re-read by a later load. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export function useTrialEndRefresh(
  proTrial: ProTrialView | null | undefined,
  refresh: () => unknown
): void {
  const endsAt = proTrial?.state === 'active' ? proTrial.endsAt : null;
  useEffect(() => {
    if (!endsAt) return;
    const delay = Date.parse(endsAt) - Date.now() + AFTER_END_MS;
    if (!Number.isFinite(delay) || delay > MAX_TIMEOUT_MS) return;
    let retry: ReturnType<typeof setInterval> | undefined;
    const atEnd = setTimeout(
      () => {
        void refresh();
        retry = setInterval(() => void refresh(), END_RETRY_MS);
      },
      Math.max(0, delay)
    );
    return () => {
      clearTimeout(atEnd);
      clearInterval(retry);
    };
  }, [endsAt, refresh]);
}
